import { userPool } from '../authentication/AuthContext';  // Export userPool from AuthContext

export const api = {
  makeAuthenticatedRequest: async (endpoint, { method = 'GET', body = null, responseType = 'json' } = {}) => {
    let idToken;
    
    // Try to get the user from userPool first
    const user = userPool.getCurrentUser();
    if (user) {
      // Get the session if user exists in userPool
      try {
        const session = await new Promise((resolve, reject) => {
          user.getSession((err, session) => {
            if (err) {
              reject(new Error('Authentication failed'));
            }
            else resolve(session);
          });
        });
        idToken = session.getIdToken().getJwtToken();
      } catch (error) {
        console.error('Session error:', error);
      }
    }
    
    // If no user in userPool or session error, try to use token from localStorage (for Google users)
    if (!idToken) {
      idToken = localStorage.getItem('idToken');
      
      // Validate the token before using it
      if (idToken) {
        try {
          // Basic validation of JWT format and expiration
          const tokenParts = idToken.split('.');
          if (tokenParts.length !== 3) {
            throw new Error('Invalid token format');
          }
          
          const payload = JSON.parse(atob(tokenParts[1]));
          const now = Math.floor(Date.now() / 1000);
          
          // Check if token is expired
          if (payload.exp <= now) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('refreshToken');
            throw new Error('Token expired');
          }
          
          // Verify this is a Google-authenticated user
          if (payload.profile !== 'google') {
            // If using localStorage for non-Google users, that could be suspicious
            throw new Error('Invalid authentication method');
          }
        } catch (error) {
          console.error('Token validation error:', error);
          idToken = null;
        }
      }
      
      if (!idToken) {
        console.error('No valid authentication token available');
        throw new Error('No user logged in');
      }
    }
    
    // Make the request with the token
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        mode: 'cors',
        body: body ? JSON.stringify(body) : null
      });
    
      if (!response.ok) {
        // Try to parse the error as JSON first
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // If not JSON, get the text
          const text = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, message: ${text}`);
        }
        
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      if (responseType === 'blob') {
        return response.blob();
      }
      
      return response.json();
    } catch (error) {
      throw error;
    }
  },

  makePublicRequest: async (endpoint, { method = 'GET', body = null } = {}) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          // NO Authorization header here
        },
        mode: 'cors',
        body: body ? JSON.stringify(body) : null
      });

      if (!response.ok) {
        let errorData;
        try {
            errorData = await response.json();
        } catch (e) {
            const text = await response.text();
            throw new Error(`HTTP error! status: ${response.status}, message: ${text}`);
        }
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      throw error;
    }
  }
};